/**
 * Client-side recurrence helpers. Bridges between the user-friendly preset
 * picker (Once / Daily / Weekly / Monthly / Yearly / Custom) and the RFC-5545
 * RRULE strings stored on the event row. Also provides a *preview* expander
 * that shows the next N occurrences in the form so the author sees what the
 * pattern will produce before saving.
 *
 * Mirrors the server-side expander at `server/src/lib/recurrence.ts` for the
 * subset we expose. Keep the two files in lockstep when extending.
 */

export type RecurrenceFreq = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM';

export type WeekdayCode = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export const WEEKDAY_LABELS: Record<WeekdayCode, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

export interface RecurrenceState {
  freq: RecurrenceFreq;
  /** Repeat every N intervals — 1 = every, 2 = every other, etc. */
  interval: number;
  /** WEEKLY: which weekdays the event lands on. */
  byDay: WeekdayCode[];
  /** MONTHLY: which day of the month (1–31). */
  byMonthDay: number | null;
  /** Number of occurrences cap (mutually exclusive with `until`). */
  count: number | null;
  /** End date (`YYYY-MM-DD`) cap (mutually exclusive with `count`). */
  until: string | null;
  /** When `freq === 'CUSTOM'`, the raw RRULE string the user typed. */
  custom: string;
  /** Skipped occurrence dates (`YYYYMMDD`) — the server's EXDATE part. The
   *  picker has no control for them; they are carried through so saving an
   *  edited event does not quietly un-cancel a week. */
  exDates: string[];
}

export const NO_RECURRENCE: RecurrenceState = {
  freq: 'NONE',
  interval: 1,
  byDay: [],
  byMonthDay: null,
  count: null,
  until: null,
  custom: '',
  exDates: [],
};

/** Convert a stored RRULE (or empty / null) back into the preset state. */
export function parseRecurrence(rule: string | null | undefined, anchor?: Date): RecurrenceState {
  if (!rule || !rule.trim()) return NO_RECURRENCE;
  const body = rule.trim().replace(/^RRULE:/i, '');
  const parts = new Map<string, string>();
  for (const seg of body.split(';')) {
    const [k, v] = seg.split('=');
    if (k && v !== undefined) parts.set(k.toUpperCase(), v);
  }
  const freqRaw = parts.get('FREQ');
  const freq: RecurrenceFreq =
    freqRaw === 'DAILY' || freqRaw === 'WEEKLY' || freqRaw === 'MONTHLY' || freqRaw === 'YEARLY'
      ? freqRaw
      : 'CUSTOM';
  if (freq === 'CUSTOM') {
    return { ...NO_RECURRENCE, freq: 'CUSTOM', custom: body };
  }
  const interval = Math.max(1, Number(parts.get('INTERVAL') ?? '1') || 1);
  const byDay: WeekdayCode[] = (() => {
    const v = parts.get('BYDAY');
    if (!v) return [];
    return v.split(',').map((s) => s.trim().toUpperCase()).filter(isWeekdayCode);
  })();
  const byMonthDay = (() => {
    const v = parts.get('BYMONTHDAY');
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  })();
  const count = (() => {
    const v = parts.get('COUNT');
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const until = (() => {
    const v = parts.get('UNTIL');
    if (!v) return null;
    const m = /^(\d{4})(\d{2})(\d{2})/.exec(v);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    return null;
  })();
  // Default BYDAY to the anchor's weekday if a weekly rule omitted it.
  const finalByDay =
    freq === 'WEEKLY' && byDay.length === 0 && anchor ? [weekdayCodeFor(anchor)] : byDay;
  const exDates = (parts.get('EXDATE') ?? '')
    .split(',')
    .map((s) => /^(\d{8})/.exec(s.trim().replace(/-/g, ''))?.[1])
    .filter((s): s is string => !!s);
  return { freq, interval, byDay: finalByDay, byMonthDay, count, until, custom: '', exDates };
}

/** Serialise the preset state back to an RRULE string. Returns `null` for
 *  freq === 'NONE'. Returns the custom raw text when freq === 'CUSTOM'. */
export function serialiseRecurrence(state: RecurrenceState): string | null {
  if (state.freq === 'NONE') return null;
  if (state.freq === 'CUSTOM') return state.custom.trim() || null;
  const segs: string[] = [`FREQ=${state.freq}`];
  if (state.interval && state.interval > 1) segs.push(`INTERVAL=${state.interval}`);
  if (state.freq === 'WEEKLY' && state.byDay.length > 0) {
    segs.push(`BYDAY=${state.byDay.join(',')}`);
  }
  if (state.freq === 'MONTHLY' && state.byMonthDay) {
    segs.push(`BYMONTHDAY=${state.byMonthDay}`);
  }
  if (state.count) segs.push(`COUNT=${state.count}`);
  else if (state.until) segs.push(`UNTIL=${state.until.replace(/-/g, '')}T235959Z`);
  if (state.exDates.length > 0) segs.push(`EXDATE=${state.exDates.join(',')}`);
  return segs.join(';');
}

/** Expand the next `n` occurrences from `anchor` for a UI preview. Mirrors
 *  the server expander but is local to the form so the author gets instant
 *  feedback. */
export function previewNextOccurrences(
  state: RecurrenceState,
  anchor: Date,
  n = 5,
): Date[] {
  if (state.freq === 'NONE') return [anchor];
  if (state.freq === 'CUSTOM') {
    // We don't try to parse arbitrary custom rules locally — the server is
    // authoritative. Show only the anchor; a power user knows what they wrote.
    return [anchor];
  }
  const out: Date[] = [];
  const max = Math.min(n, state.count ?? n, 12);
  if (state.freq === 'WEEKLY' && state.byDay.length > 0) {
    const baseWeek = startOfWeek(anchor);
    let weekStart = new Date(baseWeek);
    let safety = 0;
    while (out.length < max && safety++ < 52) {
      for (const day of state.byDay) {
        const dayIdx = weekdayIndex(day);
        const occ = applyTime(addDays(weekStart, dayIdx), anchor);
        if (occ.getTime() < anchor.getTime()) continue;
        if (state.until && occ.getTime() > untilToMs(state.until)) return out;
        if (!state.exDates.includes(compactDate(occ))) out.push(occ);
        if (out.length >= max) break;
      }
      weekStart = addDays(weekStart, 7 * state.interval);
    }
    return out;
  }
  let cursor = new Date(anchor);
  while (out.length < max) {
    if (state.until && cursor.getTime() > untilToMs(state.until)) break;
    if (!state.exDates.includes(compactDate(cursor))) out.push(cursor);
    cursor = stepCursor(cursor, state);
  }
  return out;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function compactDate(d: Date): string {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function isWeekdayCode(s: string): s is WeekdayCode {
  return s === 'MO' || s === 'TU' || s === 'WE' || s === 'TH' || s === 'FR' || s === 'SA' || s === 'SU';
}

function weekdayCodeFor(d: Date): WeekdayCode {
  const codes: WeekdayCode[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  return codes[d.getDay()]!;
}

function weekdayIndex(c: WeekdayCode): number {
  // 0 = Monday in the week-starts-on-Mon layout used everywhere else.
  const order: WeekdayCode[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  return order.indexOf(c);
}

function startOfWeek(d: Date): Date {
  const dow = d.getDay();
  const offset = (dow + 6) % 7;
  const out = new Date(d);
  out.setDate(out.getDate() - offset);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function applyTime(day: Date, anchor: Date): Date {
  const out = new Date(day);
  out.setHours(anchor.getHours(), anchor.getMinutes(), anchor.getSeconds(), 0);
  return out;
}

function stepCursor(cursor: Date, s: RecurrenceState): Date {
  const out = new Date(cursor);
  switch (s.freq) {
    case 'DAILY':
      out.setDate(out.getDate() + s.interval);
      return out;
    case 'WEEKLY':
      out.setDate(out.getDate() + 7 * s.interval);
      return out;
    case 'MONTHLY': {
      const targetDay = s.byMonthDay ?? cursor.getDate();
      out.setMonth(out.getMonth() + s.interval);
      const lastDay = new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate();
      out.setDate(Math.min(targetDay, lastDay));
      return out;
    }
    case 'YEARLY':
      out.setFullYear(out.getFullYear() + s.interval);
      return out;
    default:
      return out;
  }
}

function untilToMs(until: string): number {
  return Date.parse(`${until}T23:59:59`);
}

/** Friendly summary for the UI ("Every Monday and Wednesday until Apr 30"). */
export function summariseRecurrence(state: RecurrenceState): string {
  if (state.freq === 'NONE') return 'Does not repeat';
  if (state.freq === 'CUSTOM') return state.custom ? `Custom: ${state.custom}` : 'Custom (not set)';
  const cadence = (() => {
    const i = state.interval;
    if (state.freq === 'DAILY') return i === 1 ? 'Every day' : `Every ${i} days`;
    if (state.freq === 'WEEKLY') {
      const days = state.byDay.length > 0 ? state.byDay.map((d) => WEEKDAY_LABELS[d]).join(', ') : '';
      const base = i === 1 ? 'Every week' : `Every ${i} weeks`;
      return days ? `${base} on ${days}` : base;
    }
    if (state.freq === 'MONTHLY') {
      const day = state.byMonthDay ? ` on day ${state.byMonthDay}` : '';
      return (i === 1 ? 'Every month' : `Every ${i} months`) + day;
    }
    return i === 1 ? 'Every year' : `Every ${i} years`;
  })();
  const tail = (() => {
    if (state.count) return `, for ${state.count} occurrences`;
    if (state.until) return `, until ${state.until}`;
    return '';
  })();
  return cadence + tail;
}
